import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Text, Button } from '@lnr-v2/ui';
import type { TopCase, MetricTrend } from '@lnr-v2/types';
import { Modal } from '../../atoms/Modal';
import { MetricDetailItem } from '../../molecules/MetricDetailItem';
import styles from './PortfolioViewsModal.module.css';

export interface PortfolioViewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  totalViews: number;
  trend?: MetricTrend;
  topCases: TopCase[];
}

export const PortfolioViewsModal: React.FC<PortfolioViewsModalProps> = ({
  isOpen,
  onClose,
  totalViews,
  trend,
  topCases,
}) => {
  const navigate = useNavigate();

  const handleViewAllCases = () => {
    navigate('/admin/cases');
    onClose();
  };

  const handleCaseClick = (caseId: string) => {
    navigate(`/admin/cases/${caseId}/edit`);
    onClose();
  };

  const renderTrendIcon = () => {
    if (!trend) return null;

    const icons = {
      up: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconUp}
        >
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      down: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconDown}
        >
          <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
          <polyline points="17 18 23 18 23 12" />
        </svg>
      ),
      neutral: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={styles.trendIconNeutral}
        >
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
    };

    return icons[trend.direction];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Portfolio Views This Month"
      size="large"
    >
      <div className={styles.container}>
        {/* Summary Section */}
        <div className={styles.summary}>
          <div className={styles.totalViews}>
            <Heading as="h3" className={styles.viewsNumber}>
              {totalViews.toLocaleString()}
            </Heading>
            <Text size="sm" className={styles.viewsLabel}>
              Total Views
            </Text>
          </div>
          {trend && (
            <div className={styles.trend}>
              <div className={styles.trendIndicator}>
                {renderTrendIcon()}
                <Text size="sm" weight="semibold" className={styles.trendValue}>
                  {trend.value}
                </Text>
              </div>
              <Text size="xs" className={styles.trendLabel}>
                {trend.label}
              </Text>
            </div>
          )}
        </div>

        {/* Top Cases Section */}
        <div className={styles.section}>
          <Heading as="h4" size="md" className={styles.sectionTitle}>
            Top Viewed Cases
          </Heading>

          {topCases.length === 0 ? (
            <div className={styles.emptyState}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                className={styles.emptyIcon}
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <Text size="md" weight="semibold" className={styles.emptyTitle}>
                No portfolio views yet this month
              </Text>
              <Text size="sm" className={styles.emptySubtitle}>
                Check back later or publish new cases to start tracking views
              </Text>
            </div>
          ) : (
            <div className={styles.casesList}>
              {topCases.map((caseItem) => (
                <MetricDetailItem
                  key={caseItem.id}
                  image={caseItem.image}
                  imageAlt={caseItem.title}
                  title={caseItem.title}
                  subtitle={caseItem.clientName}
                  metric={caseItem.viewCount}
                  metricLabel="views"
                  onClick={() => handleCaseClick(caseItem.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleViewAllCases}>
            View All Cases →
          </Button>
        </div>
      </div>
    </Modal>
  );
};
